import { useState, Suspense } from 'react';
import { lazyWithReload } from '@/shared/utils/lazyWithReload';
import { Toaster } from "@/theme/components/ui/sonner";
import { TooltipProvider } from "@/theme/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "./theme";
import { AuthProvider } from "@/core/auth/AuthContext";
import { ProtectedRoute } from "@/core/components/ProtectedRoute";
import { AccessDeniedRedirect } from "@/core/components/AccessDeniedRedirect";
import { ModuleUnavailableRedirect } from "@/core/components/ModuleUnavailableRedirect";
import { useLocation as useLocationEntity } from "@/entities/location/hooks/useLocations";
import { useActiveLocationModules } from "@/entities/location/hooks/useActiveLocationModules";
import { isCommercialModuleEnabled } from "@/entities/location/utils/moduleGating";
import type { CommercialModule } from "@/entities/location/types";
import LoginPage from "@/core/pages/LoginPage";
// LoginPage stays eager — it's the entry point. The rest are reached
// only after navigation and don't need to live in the initial bundle.
const UnsubscribePage = lazyWithReload(() => import("@/core/pages/UnsubscribePage"));
const DownloadsPage = lazyWithReload(() => import("@/core/pages/DownloadsPage"));
const LogoutPage = lazyWithReload(() => import("@/core/pages/LogoutPage"));
const NotFoundPage = lazyWithReload(() => import("@/core/pages/NotFoundPage"));
const PermissionDeniedPage = lazyWithReload(() => import("@/core/pages/PermissionDeniedPage"));
const ModuleUnavailablePage = lazyWithReload(() => import("@/core/pages/ModuleUnavailablePage"));
const SessionExpiredPage = lazyWithReload(() => import("@/core/pages/SessionExpiredPage"));
const AccountSuspendedPage = lazyWithReload(() => import("@/core/pages/AccountSuspendedPage"));
import { i18n } from "@/core/i18n";
import { TenantProvider } from "@/core/tenant";
import { ActiveContextProvider, readLastScopeHint, useActiveContext } from "@/shared/hooks/useActiveContext";
import { ReporterContextSync } from "@/core/observability/ReporterContextSync";
import { ErrorBoundary, LoadingState } from "@/shared/components";
import { readLastLocationOverviewPath, resolveDefaultLocationOverviewTail } from "@/shared/services/locationOverviewRouting";
import { AppLayout } from "@/components/AppLayout";
import { CompanyLayout } from "@/entities/company/components/CompanyLayout";
import { ConsentBanner } from "@/shared/components/ConsentBanner";
import { GatewayRedirectGuard } from "@/shared/components/GatewayRedirectGuard";
import { TreasuryAccessGate } from '@/modules/treasury/components/TreasuryAccessGate';
import { useAuth } from '@/core/auth/useAuth';
import { getAccessibleLocationIds, hasAllLocationAccess } from '@/core/auth/permissions';
import {
  canAccessCompanyScope,
  canViewActivities,
  canManageCompanyIntegrations,
  canViewPlatformInvoices,
  canManageCompanyLocations,
  canManageLocationSettings,
  canManageCompanyUsers,
  canManageUsersAtScope,
  canManageInventory,
  canManagePayrollModule,
  canManagePOS,
  canManageSuppliers,
  canViewAccommodations,
  canViewBookings,
  canViewGuests,
  canViewPOSSales,
  canViewShifts,
  canViewStaffModule,
} from '@/core/auth/modulePermissions';

// Lazy load all pages for code splitting
const ManagementOverviewPage = lazyWithReload(() => import("./pages/ManagementOverviewPage"));
const FrontdeskOverviewPage = lazyWithReload(() => import("./pages/FrontdeskOverviewPage"));
const ProductsSalesOverviewPage = lazyWithReload(() => import("./pages/ProductsSalesOverviewPage"));
const HROverviewPage = lazyWithReload(() => import("./pages/HROverviewPage"));
const BookingsPage = lazyWithReload(() => import("./pages/BookingsPage"));
const BookingNewPage = lazyWithReload(() => import("./pages/BookingNewPage"));
const BookingCheckinPage = lazyWithReload(() => import("./pages/BookingCheckinPage"));
const BookingStatementPage = lazyWithReload(() => import("./pages/BookingStatementPage"));
const PersonProfilePage = lazyWithReload(() => import("./pages/PersonProfilePage"));
const MyProfilePage = lazyWithReload(() => import("./pages/MyProfilePage"));
const HelpPage = lazyWithReload(() => import("./pages/HelpPage"));
const StaffPage = lazyWithReload(() => import("./pages/StaffPage"));
const StaffPositionsPage = lazyWithReload(() => import("./pages/StaffPositionsPage"));
const POSPage = lazyWithReload(() => import("./pages/POSPage"));
const GuestsPage = lazyWithReload(() => import("./pages/GuestsPage"));
const GuestOfferingsPage = lazyWithReload(() => import("./pages/GuestOfferingsPage"));
const ShiftsPage = lazyWithReload(() => import("./pages/ShiftsPage"));
const TreasuryPage = lazyWithReload(() => import("./pages/TreasuryPage"));
const TreasuryOverviewPage = lazyWithReload(() => import("./pages/TreasuryOverviewPage"));
const TreasuryBankingPage = lazyWithReload(() => import("./pages/TreasuryBankingPage"));
const TreasuryPendingPage = lazyWithReload(() => import("./pages/TreasuryPendingPage"));
const TreasuryCashPage = lazyWithReload(() => import("./pages/TreasuryCashPage"));
const TreasuryReportsPage = lazyWithReload(() => import("./pages/TreasuryReportsPage"));
const LocationsPage = lazyWithReload(() => import("./pages/LocationsPage"));
const BookingDetailPage = lazyWithReload(() => import("@/modules/booking/components/BookingDetailPage").then(m => ({ default: m.BookingDetailPage })));
const TermsPage = lazyWithReload(() => import("./pages/TermsPage"));
const PrivacyPage = lazyWithReload(() => import("./pages/PrivacyPage"));
const InvitePage = lazyWithReload(() => import("./pages/InvitePage"));
const SetPasswordPage = lazyWithReload(() => import("./pages/SetPasswordPage"));
const ResetPasswordConfirmPage = lazyWithReload(() => import("./pages/ResetPasswordConfirmPage"));
const ForgotPasswordPage = lazyWithReload(() => import("./pages/ForgotPasswordPage"));
const LocationSettingsPage = lazyWithReload(() => import("@/entities/location/pages/settings"));
const TreasurySetupPage = lazyWithReload(() => import("@/entities/treasury/pages/TreasurySetupPage"));

// Bar & Kitchen pages
const BarKitchenTodayPage = lazyWithReload(() => import("./pages/BarKitchenTodayPage"));
const BarKitchenMenusPage = lazyWithReload(() => import("./pages/BarKitchenMenusPage"));
const BarKitchenRecipesPage = lazyWithReload(() => import("./pages/BarKitchenRecipesPage"));
const BarKitchenProductsPage = lazyWithReload(() => import("./pages/BarKitchenProductsPage"));
const BarKitchenStockPage = lazyWithReload(() => import("./pages/BarKitchenStockPage"));

// Shop pages
const ShopTodayPage = lazyWithReload(() => import("./pages/ShopTodayPage"));
const ShopCatalogPage = lazyWithReload(() => import("./pages/ShopCatalogPage"));
const ShopProductsPage = lazyWithReload(() => import("./pages/ShopProductsPage"));
const ShopStockPage = lazyWithReload(() => import("./pages/ShopStockPage"));

// POS pages
const POSTerminalsPage = lazyWithReload(() => import("./pages/POSTerminalsPage"));
const POSDailyClosePage = lazyWithReload(() => import("./pages/POSDailyClosePage"));
const POSCompReasonsPage = lazyWithReload(() => import("./pages/POSCompReasonsPage"));

// Sales — one page, tabs in ?tab= (transactions · open · adjustments · reports)
const SalesPage = lazyWithReload(() => import("./pages/SalesPage"));

// Activities pages
const ActivitiesPageV2 = lazyWithReload(() => import("./pages/ActivitiesPageV2"));

// Services (frontdesk) — sibling of Activities; catalog / runs / providers tabs.
const ServicesPage = lazyWithReload(() => import("./pages/ServicesPage"));

// Rooms
const RoomsPage = lazyWithReload(() => import("./pages/RoomsPage"));

const NotificationsPage = lazyWithReload(() => import("./pages/NotificationsPage"));
const LocationNotificationsPage = lazyWithReload(() => import("./pages/LocationNotificationsPage"));
const NotificationPreferencesPage = lazyWithReload(() => import("@/modules/notifications/pages/NotificationPreferencesPage"));
const GuestStatementPage = lazyWithReload(() => import("./pages/GuestStatementPage"));
const PayrollPage = lazyWithReload(() => import("./pages/PayrollPage"));
const TreasuryReconciliationPage = lazyWithReload(() => import("./pages/TreasuryReconciliationPage"));
const SuppliersPage = lazyWithReload(() => import("./pages/SuppliersPage"));
const SuppliersPurchaseOrdersPage = lazyWithReload(() => import("./pages/SuppliersPurchaseOrdersPage"));
const SuppliersHistoryPage = lazyWithReload(() => import("./pages/SuppliersHistoryPage"));
const PersonsPage = lazyWithReload(() => import("./pages/PersonsPage"));
const GatewayPage = lazyWithReload(() => import("./pages/GatewayPage"));
const BookinglayerConnectPage = lazyWithReload(() => import("./pages/BookinglayerConnectPage"));
const BookinglayerStatusPage = lazyWithReload(() => import("./pages/BookinglayerStatusPage"));
const StripeConnectPage = lazyWithReload(() => import("./pages/StripeConnectPage"));
import { STRIPE_AVAILABLE } from "@/modules/integrations/availability";
const StripeLedgerPage = lazyWithReload(() => import("@/modules/treasury/pages/StripeLedgerPage").then(m => ({ default: m.StripeLedgerPage })));
const StripePayoutsPage = lazyWithReload(() => import("@/modules/treasury/pages/StripePayoutsPage").then(m => ({ default: m.StripePayoutsPage })));
const CompanyLocationsPage = lazyWithReload(() => import("@/entities/company/pages/CompanyLocationsPage"));
const CompanyLocationDetailPage = lazyWithReload(() => import("@/entities/company/pages/CompanyLocationDetailPage"));
const CompanyLocationSettingsPage = lazyWithReload(() => import("@/entities/company/pages/locationSettings/CompanyLocationSettingsPage"));
const CompanySettingsPage = lazyWithReload(() => import("@/entities/company/pages/CompanySettingsPage"));
const CompanyPeoplePage = lazyWithReload(() => import("@/entities/company/pages/CompanyPeoplePage"));
const CompanyStaffPositionsPage = lazyWithReload(() => import("@/entities/company/pages/CompanyStaffPositionsPage"));
const CompanyPayrollPage = lazyWithReload(() => import("@/entities/company/pages/CompanyPayrollPage"));
const CompanyTreasuryPage = lazyWithReload(() => import("@/entities/company/pages/CompanyTreasuryPage"));
const CompanyInvoicesPage = lazyWithReload(() => import("@/entities/company/pages/CompanyInvoicesPage"));
const LocationInvoicesPage = lazyWithReload(() => import("@/entities/location/pages/LocationInvoicesPage"));
const CompanyUsersPage = lazyWithReload(() => import("@/entities/company/pages/CompanyUsersPage"));
const CompanyNotificationsPage = lazyWithReload(() => import("@/entities/company/pages/CompanyNotificationsPage"));
const CompanyUserProfilePage = lazyWithReload(() => import("@/entities/company/pages/CompanyUserProfilePage"));
const LocationUsersPage = lazyWithReload(() => import("@/entities/location/pages/LocationUsersPage"));
const LocationOverviewPage = lazyWithReload(() => import("@/entities/location/pages/LocationOverviewPage"));
const TokensLocationPage = lazyWithReload(() =>
  import("@/modules/tokens/pages/TokensLocationPage").then(m => ({ default: m.TokensLocationPage }))
);
const TokensCompanyPage = lazyWithReload(() =>
  import("@/modules/tokens/pages/TokensCompanyPage").then(m => ({ default: m.TokensCompanyPage }))
);

// Dev-only: design tokens reference page. Not gated, not i18n'd — redesign scaffolding.
const DevTokensPage = lazyWithReload(() => import("./pages/DevTokensPage"));

const APP_LOADING_MESSAGE = i18n.t('common.loading', { defaultValue: 'Loading...' });

/**
 * Helper: redirect a legacy unprefixed location route to its `/location/*`
 * equivalent. Used by the back-compat redirect routes registered just above
 * the catch-all NotFoundPage. The legacy URLs (`/bookings`, `/treasury`, etc.)
 * were migrated to `/location/...` on 2026-04-25; existing bookmarks would
 * break without these splat redirects, so we preserve search + hash too.
 */
function RedirectToLocationPath() {
  const loc = useLocation();
  return (
    <Navigate
      to={`/location${loc.pathname}${loc.search}${loc.hash}`}
      replace
    />
  );
}

/**
 * Forward the pre-scope-in-URL Stripe ledger/payouts URLs into the
 * `/location/...` namespace, preserving `?integrationId=`. The catch-all
 * below resolves the missing location id from the last-seen scope hint.
 */
function LegacyStripeRedirect() {
  const loc = useLocation();
  return <Navigate to={`/location${loc.pathname}${loc.search}${loc.hash}`} replace />;
}

/** Forward legacy /persons/:personId to /company/persons/:personId. */
function LegacyPersonsRedirect() {
  const loc = useLocation();
  return (
    <Navigate
      to={`/company${loc.pathname}${loc.search}${loc.hash}`}
      replace
    />
  );
}

/**
 * Catch-all redirect for any `/location/<rest>` URL that didn't match
 * a registered route. If `<rest>` looks UUID-shaped we assume it's
 * already in the new shape and the resource itself is missing → 404.
 * Otherwise we treat the URL as legacy and rewrite via the last-seen
 * location hint, falling back to `/select`.
 */
function LegacyLocationCatchAll() {
  const loc = useLocation();
  const hint = readLastScopeHint();
  const rest = loc.pathname.slice('/location/'.length);
  const firstSegment = rest.split('/')[0];
  // Cheap "looks like a UUID" check — enough to detect the new shape.
  if (/^[0-9a-f]{8}-/i.test(firstSegment)) {
    return <Navigate to="/404" replace />;
  }
  if (hint?.type === 'location' && hint.id) {
    return (
      <Navigate
        to={`/location/${hint.id}/${rest}${loc.search}${loc.hash}`}
        replace
      />
    );
  }
  const ret = encodeURIComponent(`${loc.pathname}${loc.search}${loc.hash}`);
  return <Navigate to={`/select?return=${ret}`} replace />;
}

/** Helper: wrap a page in ProtectedRoute + AppLayout + Suspense + GatewayRedirect */
function ProtectedPage({ children, fallback = APP_LOADING_MESSAGE }: { children: React.ReactNode; fallback?: string }) {
  return (
    <ProtectedRoute>
      <GatewayRedirectGuard>
        <AppLayout>
          <Suspense fallback={<LoadingState message={fallback} />}>
            {children}
          </Suspense>
        </AppLayout>
      </GatewayRedirectGuard>
    </ProtectedRoute>
  );
}

function ProtectedStandalonePage({ children, fallback = APP_LOADING_MESSAGE }: { children: React.ReactNode; fallback?: string }) {
  return (
    <ProtectedRoute>
      <Suspense fallback={<LoadingState message={fallback} />}>
        {children}
      </Suspense>
    </ProtectedRoute>
  );
}

function FunctionalLocationPageGuard({
  allowed,
  children,
  fallback = APP_LOADING_MESSAGE,
  fallbackTo,
  standalone = false,
  missing,
  scope,
}: {
  allowed: boolean;
  children: React.ReactNode;
  fallback?: string;
  /**
   * Optional silent-bounce destination. When omitted (the default) a
   * denied access renders the editorial 403 page (`/access-denied`)
   * with the original URL preserved as `?return=`. Pass an explicit
   * path only when a silent bounce is the intended UX (e.g. an
   * always-redirect location switcher).
   */
  fallbackTo?: string;
  standalone?: boolean;
  /** Permission codename forwarded to the editorial 403 diagnostic. */
  missing?: string;
  /** Display label for the scope (e.g. location name). */
  scope?: string;
}) {
  return (
    <ProtectedRoute>
      <GatewayRedirectGuard>
        {allowed ? (
          standalone ? (
            <Suspense fallback={<LoadingState message={fallback} />}>
              {children}
            </Suspense>
          ) : (
            <AppLayout>
              <Suspense fallback={<LoadingState message={fallback} />}>
                {children}
              </Suspense>
            </AppLayout>
          )
        ) : fallbackTo ? (
          <Navigate to={fallbackTo} replace />
        ) : (
          <AccessDeniedRedirect missing={missing} scope={scope} />
        )}
      </GatewayRedirectGuard>
    </ProtectedRoute>
  );
}

/**
 * The location-scoped surfaces `ModulePageGuard` knows how to gate. The
 * three "domain" entries (`inventory`, `commerceOverview`, `hrOverview`)
 * replaced the old `ModuleProtectedPage`, which checked only that the
 * commercial module was switched on — the rail hid those entries behind an
 * RBAC gate but a typed URL rendered them anyway.
 */
type GuardedModule =
  | 'bookings' | 'guests' | 'accommodations' | 'activities' | 'pos' | 'sales'
  | 'staff' | 'shifts' | 'payroll' | 'suppliers' | 'users'
  | 'inventory' | 'commerceOverview' | 'hrOverview' | 'frontdeskOverview'
  | 'invoices';

/**
 * Canonical permission codename per location-scoped module — surfaced in
 * the editorial 403 diagnostic pill so denied users see exactly which
 * permission they lack. The mapping picks the most specific codename
 * the corresponding `canX` helper accepts (the broadest entry point in
 * the OR list); `company.full_access` is intentionally omitted since
 * that's the bypass token, not the missing thing.
 */
const MODULE_CODENAME: Record<GuardedModule, string> = {
  bookings: 'location.view_bookings',
  guests: 'location.manage_guests',
  accommodations: 'location.manage_accommodations',
  activities: 'location.view_activities',
  pos: 'location.manage_pos',
  // Sales is a reporting surface, not an operator one: reading the ledger
  // must not require the ability to settle, void or refund. Mirrors
  // CanViewOrManagePOSSales server-side.
  sales: 'location.view_pos_sales',
  staff: 'location.view_staff',
  shifts: 'location.view_staff',
  payroll: 'location.edit_staff',
  suppliers: 'location.manage_inventory',
  // Platform invoices: `location.view_invoices` is the per-location grant
  // canViewPlatformInvoices checks, and the one the 403 page should name.
  invoices: 'location.view_invoices',
  // /location/:locationId/users mirrors the company-scope surface but
  // inside the location chrome, and it is a *location* surface: a site
  // Manager administers their own people with `location.manage_users`,
  // without holding tenant-wide administration. The company codename
  // still opens it (checked in the guard); this is the codename the 403
  // page should name, because it is the one the operator is missing.
  users: 'location.manage_users',
  // Bar & Kitchen, Retail and the standalone Inventory page all read
  // purchase costs and recipe composition. Same codename the rail uses.
  inventory: 'location.manage_inventory',
  commerceOverview: 'location.manage_inventory',
  hrOverview: 'location.view_staff',
  frontdeskOverview: 'location.view_bookings',
};

/**
 * Commercial-module activation per location-scoped guard `module`. A route
 * is reachable only when its module is switched ON for the active location
 * (RBAC permission is checked separately). `null` means the surface belongs
 * to the always-on base (`core_stay`) or is a tenant-level surface (users)
 * that location module toggles never gate. Keep in sync with
 * `PERMISSION_MODULES_BY_COMMERCIAL` in entities/location/utils/moduleGating.
 */
const MODULE_COMMERCIAL: Record<GuardedModule, CommercialModule[] | null> = {
  bookings: null,
  guests: null,
  accommodations: null,
  activities: ['activities'],
  pos: ['pos_commerce'],
  sales: ['pos_commerce'],
  staff: ['hr'],
  shifts: ['hr'],
  payroll: ['hr'],
  suppliers: ['inventory'],
  users: null,
  // Billing is not a commercial module — a location with every module off is
  // still invoiced, and must still be able to read the bill.
  invoices: null,
  inventory: ['inventory'],
  // OR-semantics: the Commerce overview aggregates both halves, so either
  // module being on is enough to make the page meaningful.
  commerceOverview: ['pos_commerce', 'inventory'],
  hrOverview: ['hr'],
  // Frontdesk lives in the always-on `core_stay` base — no module toggle.
  frontdeskOverview: null,
};

const COMPANY_MODULE_CODENAME: Record<
  'users' | 'locations' | 'integrations' | 'staff' | 'payroll' | 'invoices',
  string
> = {
  users: 'company.manage_users',
  locations: 'company.manage_locations',
  integrations: 'company.manage_integrations',
  staff: 'location.view_staff',
  payroll: 'location.edit_staff',
  invoices: 'company.view_invoices',
};

function ModulePageGuard({
  module,
  children,
  fallback = APP_LOADING_MESSAGE,
  fallbackTo,
  standalone = false,
}: {
  module: GuardedModule;
  children: React.ReactNode;
  fallback?: string;
  /**
   * Optional silent-bounce destination forwarded to the inner
   * FunctionalLocationPageGuard. Omit to surface the editorial 403
   * page (`/access-denied`) — the default UX after row 27.
   */
  fallbackTo?: string;
  standalone?: boolean;
}) {
  const { user } = useAuth();
  const { activeLocationId } = useActiveContext();
  // Light fetch — almost always cached because most pages already
  // resolve the active location. Used only to populate the editorial
  // 403 / module-off page's `?scope=` diagnostic pill on a denial.
  const { data: activeLocation } = useLocationEntity(activeLocationId ?? undefined);

  // Commercial-module gate — a surface whose module is switched OFF for
  // this location is unreachable even when RBAC would allow it. `null`
  // modules (company scope / still loading) stay optimistically open.
  const activeModules = useActiveLocationModules();
  const commercialModules = MODULE_COMMERCIAL[module];
  const moduleEnabled =
    commercialModules === null ||
    activeModules === null ||
    commercialModules.some((m) => isCommercialModuleEnabled(activeModules, m));

  if (!moduleEnabled && commercialModules !== null) {
    return <ModuleUnavailableRedirect module={commercialModules[0]} scope={activeLocation?.name} />;
  }

  const allowed = module === 'bookings'
    ? canViewBookings(user, activeLocationId)
    : module === 'guests'
      ? canViewGuests(user, activeLocationId)
      : module === 'accommodations'
        ? canViewAccommodations(user, activeLocationId)
        : module === 'activities'
          ? canViewActivities(user, activeLocationId)
          : module === 'pos'
            ? canManagePOS(user, activeLocationId)
            : module === 'sales'
              ? canViewPOSSales(user, activeLocationId)
              : module === 'staff'
              ? canViewStaffModule(user, activeLocationId)
              : module === 'shifts'
                ? canViewShifts(user, activeLocationId)
                : module === 'payroll'
                  ? canManagePayrollModule(user, activeLocationId)
                  : module === 'users'
                    ? canManageUsersAtScope(user, activeLocationId)
                    : module === 'inventory'
                      ? canManageInventory(user, activeLocationId)
                      : module === 'commerceOverview'
                        ? canManagePOS(user, activeLocationId)
                          || canViewPOSSales(user, activeLocationId)
                          || canManageInventory(user, activeLocationId)
                        : module === 'hrOverview'
                          ? canViewStaffModule(user, activeLocationId)
                            || canViewShifts(user, activeLocationId)
                            || canManagePayrollModule(user, activeLocationId)
                          : module === 'frontdeskOverview'
                            ? canViewBookings(user, activeLocationId)
                              || canViewGuests(user, activeLocationId)
                              || canViewAccommodations(user, activeLocationId)
                              || canViewActivities(user, activeLocationId)
                            : module === 'invoices'
                              ? canViewPlatformInvoices(user, activeLocationId)
                              : canManageSuppliers(user, activeLocationId);

  return (
    <FunctionalLocationPageGuard
      allowed={allowed}
      fallback={fallback}
      fallbackTo={fallbackTo}
      standalone={standalone}
      missing={MODULE_CODENAME[module]}
      scope={activeLocation?.name}
    >
      {children}
    </FunctionalLocationPageGuard>
  );
}

/**
 * Guard for location surfaces that carry no permission of their own but
 * still belong to ONE location — the location notification inbox being the
 * case: the server filters every row by the permission guarding its
 * category, so the page needs no codename, but it must still refuse a
 * location the operator has no access to. Without this, hand-editing the
 * id rendered another site's chrome (breadcrumb, name) plus a spray of
 * 403 toasts instead of the editorial 403.
 */
function LocationAccessPageGuard({
  children,
  fallback = APP_LOADING_MESSAGE,
}: {
  children: React.ReactNode;
  fallback?: string;
}) {
  const { user } = useAuth();
  const { activeLocationId } = useActiveContext();
  const { data: activeLocation } = useLocationEntity(activeLocationId ?? undefined);
  const allowed =
    Boolean(activeLocationId)
    && (hasAllLocationAccess(user)
      || getAccessibleLocationIds(user).includes(activeLocationId as string));

  return (
    <FunctionalLocationPageGuard
      allowed={allowed}
      fallback={fallback}
      scope={activeLocation?.name}
    >
      {children}
    </FunctionalLocationPageGuard>
  );
}

function TreasuryPageGuard({
  children,
  capability = 'view',
  scopeType,
  fallback = APP_LOADING_MESSAGE,
}: {
  children: React.ReactNode;
  capability?: 'view' | 'manage' | 'banking' | 'reconciliation';
  scopeType?: 'company' | 'location';
  fallback?: string;
}) {
  // Commercial-module gate (location scope only). Treasury switched OFF
  // for a location makes all of its surfaces unreachable even when RBAC
  // would allow them — the company-scope treasury view is never gated
  // this way (it aggregates across locations). `null` modules (loading)
  // stay optimistically open.
  const { activeLocationId } = useActiveContext();
  const { data: activeLocation } = useLocationEntity(activeLocationId ?? undefined);
  const activeModules = useActiveLocationModules();
  const treasuryModuleOff =
    scopeType !== 'company' &&
    activeModules !== null &&
    !isCommercialModuleEnabled(activeModules, 'treasury');

  if (treasuryModuleOff) {
    return <ModuleUnavailableRedirect module="treasury" scope={activeLocation?.name} />;
  }

  // Denials fall through to the editorial 403 page via TreasuryAccessGate's
  // default. Was bouncing silently to /company/dashboard or /management/overview;
  // the editorial 403 surface is more informative.
  return (
    <ProtectedRoute>
      <TreasuryAccessGate capability={capability} scopeType={scopeType}>
        {scopeType === 'company' ? (
          <CompanyLayout>
            <Suspense fallback={<LoadingState message={fallback} />}>
              {children}
            </Suspense>
          </CompanyLayout>
        ) : (
          <GatewayRedirectGuard>
            <AppLayout>
              <Suspense fallback={<LoadingState message={fallback} />}>
                {children}
              </Suspense>
            </AppLayout>
          </GatewayRedirectGuard>
        )}
      </TreasuryAccessGate>
    </ProtectedRoute>
  );
}

/**
 * Management cluster guard — the location Management Overview surfaces
 * income, staff-count and billing KPIs, so it is a manager surface, not
 * an operational one.
 *
 * Gated on `canManageLocationSettings`: the company codename still opens
 * every camp, and `location.manage_settings` opens the one you run. It
 * was `company.manage_locations` alone until 2026-09-04, which locked a
 * site Manager out of their own overview and — because the rail still
 * showed them the Management domain via Users & Roles — turned the
 * domain click itself into a 403 (PENDINGS #49a).
 */
function ManagementPageGuard({
  children,
  fallback = APP_LOADING_MESSAGE,
}: {
  children: React.ReactNode;
  fallback?: string;
}) {
  const { user } = useAuth();
  const { activeLocationId } = useActiveContext();
  const { data: activeLocation } = useLocationEntity(activeLocationId ?? undefined);

  return (
    <FunctionalLocationPageGuard
      allowed={canManageLocationSettings(user, activeLocationId)}
      fallback={fallback}
      missing="location.manage_settings"
      scope={activeLocation?.name}
    >
      {children}
    </FunctionalLocationPageGuard>
  );
}

/**
 * Location Settings guard — the location Settings area (identity, hours,
 * seasons, monetary defaults, staff tokens, billing, integrations…) is a
 * manager surface, not an operational one, so operational roles (e.g.
 * frontdesk) can't reach it via the rail or a direct URL — they get the
 * editorial 403 instead of a page whose writes would 403 anyway.
 *
 * Same gate as the Management cluster: `company.manage_locations` for
 * every camp, `location.manage_settings` for the one you run. The
 * backend agrees — `_require_location_settings_access` accepts both,
 * keeping `activeModules`, `isActive` and the billing profiles on the
 * company side.
 */
function SettingsPageGuard({
  children,
  fallback = APP_LOADING_MESSAGE,
}: {
  children: React.ReactNode;
  fallback?: string;
}) {
  const { user } = useAuth();
  const { activeLocationId } = useActiveContext();
  const { data: activeLocation } = useLocationEntity(activeLocationId ?? undefined);

  return (
    <FunctionalLocationPageGuard
      allowed={canManageLocationSettings(user, activeLocationId)}
      fallback={fallback}
      missing="location.manage_settings"
      scope={activeLocation?.name}
    >
      {children}
    </FunctionalLocationPageGuard>
  );
}

/** Helper: wrap a company page in ProtectedRoute + CompanyLayout + Suspense.
 *
 * Gated by `canAccessCompanyScope` so location-only operators can't
 * reach the tenant-wide bare-CompanyPage surfaces (/company/overview,
 * /company/persons*, /company/tokens) via redirects or direct URL —
 * bug #5 part 2 (cross-location PII disclosure).
 *
 * Routes that already have their own narrower guard
 * (CompanyModulePageGuard, TreasuryPageGuard) are unaffected: they
 * don't use this wrapper.
 */
function CompanyPage({ children, fallback = APP_LOADING_MESSAGE }: { children: React.ReactNode; fallback?: string }) {
  const { user } = useAuth();
  return (
    <CompanyAccessGate
      allowed={canAccessCompanyScope(user)}
      fallback={fallback}
      missing="company.access"
      scope="Company"
    >
      {children}
    </CompanyAccessGate>
  );
}

function CompanyAccessGate({
  allowed,
  children,
  fallback = APP_LOADING_MESSAGE,
  fallbackTo,
  missing,
  scope,
}: {
  allowed: boolean;
  children: React.ReactNode;
  fallback?: string;
  /**
   * Optional silent-bounce destination. When omitted (the default) a
   * denied access renders the editorial 403 page (`/access-denied`)
   * with the original URL preserved as `?return=`. Pass an explicit
   * path only when a silent bounce is the intended UX.
   */
  fallbackTo?: string;
  /** Permission codename forwarded to the editorial 403 diagnostic. */
  missing?: string;
  /** Display label for the scope (e.g. "Company"). */
  scope?: string;
}) {
  return (
    <ProtectedRoute>
      {allowed ? (
        <CompanyLayout>
          <Suspense fallback={<LoadingState message={fallback} />}>
            {children}
          </Suspense>
        </CompanyLayout>
      ) : fallbackTo ? (
        <Navigate to={fallbackTo} replace />
      ) : (
        <AccessDeniedRedirect missing={missing} scope={scope} />
      )}
    </ProtectedRoute>
  );
}

function CompanyModulePageGuard({
  module,
  children,
  fallback = APP_LOADING_MESSAGE,
}: {
  module: 'users' | 'locations' | 'integrations' | 'staff' | 'payroll' | 'invoices';
  children: React.ReactNode;
  fallback?: string;
}) {
  const { user } = useAuth();

  const allowed = module === 'users'
    ? canManageCompanyUsers(user)
    : module === 'locations'
      ? canManageCompanyLocations(user)
      : module === 'integrations'
        ? canManageCompanyIntegrations(user)
        : module === 'invoices'
          ? canViewPlatformInvoices(user)
          : module === 'staff'
            ? canViewStaffModule(user, null)
          : canManagePayrollModule(user, null);

  return (
    <CompanyAccessGate allowed={allowed} fallback={fallback} missing={COMPANY_MODULE_CODENAME[module]}>
      {children}
    </CompanyAccessGate>
  );
}

// ContextualPage retired 2026-05-22 — scope-in-URL means the route
// itself determines the layout (CompanyPage for /company/*,
// ProtectedPage for /location/:locationId/*). See
// docs/plan/SCOPE_IN_URL.md §4.7.

function SettingsEntryPage() {
  const { activeContext } = useActiveContext();

  if (activeContext?.type === 'company') {
    return (
      <CompanyPage>
        <CompanySettingsPage />
      </CompanyPage>
    );
  }

  return (
    <SettingsPageGuard>
      <LocationSettingsPage />
    </SettingsPageGuard>
  );
}

function RootEntryPage() {
  const { activeContext } = useActiveContext();
  const { user } = useAuth();
  const hint = readLastScopeHint();

  // Resolve permissions for the *current* user up front and gate every
  // candidate destination against them. Without this guard the page
  // navigates to a route the access gates immediately bounce, so the
  // operator lands on /access-denied instead of somewhere useful. Most
  // visible during impersonation: the localStorage hint reflects
  // whoever logged in here last, not the impersonated user.
  const canCompany = user ? canAccessCompanyScope(user) : false;
  const canAccessAllLocations = user ? hasAllLocationAccess(user) : false;
  const accessibleLocationIds = user ? getAccessibleLocationIds(user) : [];
  const canReachLocation = (id: string) =>
    canAccessAllLocations || accessibleLocationIds.includes(id);

  // Prefer the path-derived activeContext (e.g. user already at
  // /location/{id}/...). If we're literally at "/", fall back to the
  // localStorage hint so the gateway choice from the previous session
  // is honored without bouncing through /select. Only when neither
  // path nor hint give us a *reachable* scope do we hand off to /select.
  if (activeContext?.type === 'company' && canCompany) {
    return <Navigate to="/company/overview" replace />;
  }
  if (activeContext?.type === 'location' && canReachLocation(activeContext.id)) {
    const tail = resolveDefaultLocationOverviewTail(user, activeContext.id);
    return <Navigate to={`/location/${activeContext.id}/${tail}`} replace />;
  }

  if (hint?.type === 'company' && canCompany) {
    return <Navigate to="/company/overview" replace />;
  }
  if (hint?.type === 'location' && hint.id && canReachLocation(hint.id)) {
    // readLastLocationOverviewPath returns the legacy /location/<tail> of the
    // last domain overview visited this session; inject the id from the hint
    // so we land on the new shape directly.
    const legacyPath = readLastLocationOverviewPath();
    const rememberedTail = legacyPath.startsWith('/location/')
      ? legacyPath.slice('/location/'.length)
      : null;
    // Nothing genuinely remembered this session (reset on reload) collapses to
    // the frontdesk default — but a non-frontdesk role can't operate that page,
    // so resolve their real first-accessible domain instead of the hardcoded
    // default. A truly remembered tab is honored as-is.
    const tail =
      rememberedTail && rememberedTail !== 'frontdesk/overview'
        ? rememberedTail
        : resolveDefaultLocationOverviewTail(user, hint.id);
    return <Navigate to={`/location/${hint.id}/${tail}`} replace />;
  }

  // Single-location user with no company access: auto-pick their only
  // location instead of forcing a one-item picker UI. The full picker
  // is reserved for users who actually have a choice.
  if (user) {
    if (
      !user.companyAccess
      && !canAccessAllLocations
      && accessibleLocationIds.length === 1
    ) {
      return (
        <Navigate
          to={`/location/${accessibleLocationIds[0]}/${resolveDefaultLocationOverviewTail(user, accessibleLocationIds[0])}`}
          replace
        />
      );
    }
  }

  return <Navigate to="/select" replace />;
}

function App() {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Never retry auth errors — they won't resolve with retries
          // and each attempt triggers a refresh + error toast cascade.
          const status = (error as { status?: number })?.status;
          if (status === 401 || status === 403) return false;
          return failureCount < 3;
        },
      },
      mutations: {
        retry: false,
      },
    },
  }));

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="dark">
          <AuthProvider>
            <TooltipProvider>
              <Toaster position="top-right" richColors closeButton />
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                {/* ActiveContextProvider + TenantProvider live INSIDE the
                    Router because both consume useLocation/useParams to
                    derive scope from the URL (post scope-in-URL refactor).
                    Mounting them outside throws on first render. */}
                <ActiveContextProvider>
                <TenantProvider>
                <ReporterContextSync />
                <ConsentBanner />
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/terms" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><TermsPage /></Suspense>} />
                  <Route path="/privacy" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><PrivacyPage /></Suspense>} />
                  {/* List-Unsubscribe landing — public; works for unauthenticated
                      recipients who follow an in-body link. The RFC 8058 one-click
                      POST goes directly to the backend (EMAIL_UNSUBSCRIBE_URL); this
                      page is the human-facing fallback. */}
                  <Route path="/unsubscribe" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><UnsubscribePage /></Suspense>} />
                  {/* Public downloads catalog — installers for fresh devices
                      (POS tablets, reception PCs) with no deck session yet.
                      Server-side whitelisted (bridge/pos/crew only). */}
                  <Route path="/downloads" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><DownloadsPage /></Suspense>} />

                  {/* Editorial fallback surfaces — auth-state agnostic, work pre-login. */}
                  <Route path="/access-denied" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><PermissionDeniedPage /></Suspense>} />
                  <Route path="/module-unavailable" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><ModuleUnavailablePage /></Suspense>} />
                  <Route path="/session-expired" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><SessionExpiredPage /></Suspense>} />
                  {/* Tenant has been suspended (Company.is_active=False).
                      shaka-api emits 403 + { code: 'tenant_suspended' }
                      from login + /me/, and the deck routes here so the
                      operator sees a real message instead of an empty
                      gateway. */}
                  <Route path="/account-suspended" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><AccountSuspendedPage /></Suspense>} />

                  {/* Tenant onboarding (public, anonymous) */}
                  <Route path="/invite/:token" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><InvitePage /></Suspense>} />
                  <Route path="/set-password/:token" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><SetPasswordPage /></Suspense>} />
                  {/* Password reset confirm — landed via the email
                      button sent by send_password_reset. Reads the
                      signed token from `?token=` and POSTs the new
                      password to /api/public/auth/password-reset/confirm/. */}
                  <Route path="/reset-password/confirm" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><ResetPasswordConfirmPage /></Suspense>} />

                  {/* Public "forgot password" request — emails a signed
                      reset token to the address if it matches an
                      account. Always returns success to avoid email
                      enumeration. */}
                  <Route path="/auth/forgot-password" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><ForgotPasswordPage /></Suspense>} />

                  {/* Gateway — context selector */}
                  <Route path="/select" element={<ProtectedRoute><Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><GatewayPage /></Suspense></ProtectedRoute>} />

                  {/* Root entry — RootEntryPage owns the post-login
                      decision (context, hint, or /select). Don't wrap
                      with GatewayRedirectGuard here: both would race on
                      "/" and the guard's setTimeout-style useEffect wins,
                      bouncing the user back to /select even when a hint
                      is present. */}
                  <Route
                    path="/"
                    element={(
                      <ProtectedRoute>
                        <RootEntryPage />
                      </ProtectedRoute>
                    )}
                  />

                  {/* Management (Company) — Overview is the only hub page; Today/Approvals/Alerts/Reports
                      were removed 2026-04-21 when the editorial Company Overview was introduced.
                      Approvals/Alerts signal migrated to the notification drawer; Today lives inside
                      the Location Overview timeline; Reports lives as a module entry. */}
                  {/* /management/* moved to /company/overview on 2026-04-25 — the
                      "management overview" was always a cross-location (company-scope)
                      surface mounted in the location rail by mistake. The new home is
                      under CompanyLayout. The two redirects below preserve old bookmarks. */}
                  <Route path="/management" element={<Navigate to="/company/overview" replace />} />
                  <Route path="/management/overview" element={<Navigate to="/company/overview" replace />} />

                  {/* Frontdesk */}
                  <Route path="/location/:locationId/frontdesk" element={<ModulePageGuard module="frontdeskOverview"><Navigate to="overview" replace /></ModulePageGuard>} />
                  <Route path="/location/:locationId/frontdesk/overview" element={<ModulePageGuard module="frontdeskOverview"><FrontdeskOverviewPage /></ModulePageGuard>} />

                  {/* Frontdesk > Bookings */}
                  <Route path="/location/:locationId/bookings" element={<ModulePageGuard module="bookings"><BookingsPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/bookings/new" element={<ModulePageGuard module="bookings"><BookingNewPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/bookings/checkin" element={<ModulePageGuard module="bookings"><BookingCheckinPage /></ModulePageGuard>} />
                  {/* /bookings/calendar retired 2026-04-24 — Calendar is now a tab on /bookings via BookingsCalendar shared component */}
                  <Route path="/location/:locationId/bookings/calendar" element={<Navigate to="..?tab=calendar" replace />} />
                  <Route path="/location/:locationId/bookings/:id/statement" element={<ModulePageGuard module="bookings" standalone><BookingStatementPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/bookings/:id" element={<ModulePageGuard module="bookings"><BookingDetailPage /></ModulePageGuard>} />

                  {/* Frontdesk > Guests */}
                  <Route path="/location/:locationId/guests" element={<ModulePageGuard module="guests"><GuestsPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/guests/offerings" element={<ModulePageGuard module="guests"><GuestOfferingsPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/guests/:guestId/statement" element={<ModulePageGuard module="guests" standalone><GuestStatementPage /></ModulePageGuard>} />
                  {/* Guest profile from in-location surfaces — keeps the
                      operator in location chrome instead of flipping the
                      whole app to /company/persons (bug #5). Uses
                      :personId so PersonProfilePage's useParams binds
                      straight through. */}
                  <Route path="/location/:locationId/guests/:personId" element={<ModulePageGuard module="guests"><PersonProfilePage /></ModulePageGuard>} />
                  {/* Persons → tenant-scoped under /company/persons/* per
                      docs/plan/SCOPE_IN_URL.md. Legacy /persons/* URLs
                      redirect below. */}
                  <Route path="/company/persons" element={<CompanyPage><PersonsPage /></CompanyPage>} />
                  <Route path="/company/persons/:personId" element={<CompanyPage><PersonProfilePage /></CompanyPage>} />
                  {/* Legacy /persons → /company/persons redirects. */}
                  <Route path="/persons" element={<Navigate to="/company/persons" replace />} />
                  <Route path="/persons/:personId" element={<LegacyPersonsRedirect />} />

                  {/* Frontdesk > Rooms */}
                  <Route path="/location/:locationId/rooms" element={<ModulePageGuard module="accommodations"><RoomsPage /></ModulePageGuard>} />

                  {/* Frontdesk > Activities — calendar-first hub (V2). Scheduling
                      template create/edit, scheduling and enrollment
                      management all happen in drawers inside the hub, not on
                      separate routes. Legacy new/schedule/calendar, the old
                      template detail and the enrollments routes redirect to
                      the hub. */}
                  <Route path="/location/:locationId/activities" element={<ModulePageGuard module="activities"><ActivitiesPageV2 /></ModulePageGuard>} />
                  <Route path="/location/:locationId/activities/new" element={<Navigate to=".." relative="path" replace />} />
                  <Route path="/location/:locationId/activities/schedule" element={<Navigate to=".." relative="path" replace />} />
                  <Route path="/location/:locationId/activities/calendar" element={<Navigate to=".." relative="path" replace />} />
                  <Route path="/location/:locationId/activities/registrations" element={<Navigate to=".." relative="path" replace />} />
                  <Route path="/location/:locationId/activities/instances/:activityId/enrollments" element={<Navigate to="../../.." relative="path" replace />} />
                  <Route path="/location/:locationId/activities/:id" element={<Navigate to=".." relative="path" replace />} />

                  {/* Frontdesk > Services — sibling of Activities. Reuses the
                      activities module gate (view_activities +
                      'activities' commercial module) by design; tabs live in
                      the page (?tab=providers|catalog|runs). */}
                  <Route path="/location/:locationId/services" element={<ModulePageGuard module="activities"><ServicesPage /></ModulePageGuard>} />

                  {/* Commerce > Overview (above the rail) */}
                  <Route path="/location/:locationId/products-sales" element={<ProtectedPage><Navigate to="overview" replace /></ProtectedPage>} />
                  <Route path="/location/:locationId/products-sales/overview" element={<ModulePageGuard module="commerceOverview"><ProductsSalesOverviewPage /></ModulePageGuard>} />

                  {/* Commerce > Bar & Kitchen */}
                  <Route path="/location/:locationId/bar-kitchen" element={<ModulePageGuard module="inventory"><Navigate to="today" replace /></ModulePageGuard>} />
                  <Route path="/location/:locationId/bar-kitchen/today" element={<ModulePageGuard module="inventory"><BarKitchenTodayPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/bar-kitchen/menus" element={<ModulePageGuard module="inventory"><BarKitchenMenusPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/bar-kitchen/recipes" element={<ModulePageGuard module="inventory"><BarKitchenRecipesPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/bar-kitchen/products" element={<ModulePageGuard module="inventory"><BarKitchenProductsPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/bar-kitchen/stock" element={<ModulePageGuard module="inventory"><BarKitchenStockPage /></ModulePageGuard>} />

                  {/* Commerce > Shop */}
                  <Route path="/location/:locationId/shop" element={<ModulePageGuard module="inventory"><Navigate to="today" replace /></ModulePageGuard>} />
                  <Route path="/location/:locationId/shop/today" element={<ModulePageGuard module="inventory"><ShopTodayPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/shop/catalog" element={<ModulePageGuard module="inventory"><ShopCatalogPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/shop/products" element={<ModulePageGuard module="inventory"><ShopProductsPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/shop/stock" element={<ModulePageGuard module="inventory"><ShopStockPage /></ModulePageGuard>} />

                  {/* Commerce > POS — admin tabs (workstation lives in app header) */}
                  <Route path="/location/:locationId/pos" element={<ModulePageGuard module="pos"><Navigate to="terminals" replace /></ModulePageGuard>} />
                  <Route path="/location/:locationId/pos/overview" element={<ModulePageGuard module="pos"><POSPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/pos/terminals" element={<ModulePageGuard module="pos"><POSTerminalsPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/pos/comp-reasons" element={<ModulePageGuard module="pos"><POSCompReasonsPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/pos/close" element={<ModulePageGuard module="pos"><POSDailyClosePage /></ModulePageGuard>} />

                  {/* Commerce > Sales */}
                  <Route path="/location/:locationId/sales" element={<ModulePageGuard module="sales"><SalesPage /></ModulePageGuard>} />
                  {/* Reports used to be its own route; it is a tab now. Kept as a
                      redirect so existing links and bookmarks still land. */}
                  <Route path="/location/:locationId/sales/reports" element={<Navigate to="../sales?tab=reports" replace />} />

                  {/* HR */}
                  <Route path="/location/:locationId/hr" element={<ProtectedPage><Navigate to="overview" replace /></ProtectedPage>} />
                  <Route path="/location/:locationId/hr/overview" element={<ModulePageGuard module="hrOverview"><HROverviewPage /></ModulePageGuard>} />

                  {/* HR > Staff */}
                  <Route path="/location/:locationId/staff" element={<ModulePageGuard module="staff"><StaffPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/staff/positions" element={<ModulePageGuard module="staff"><StaffPositionsPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/staff/tokens" element={<ModulePageGuard module="staff"><TokensLocationPage /></ModulePageGuard>} />
                  {/* Staff person profile from in-location surfaces —
                      mirror of /location/:locationId/guests/:personId
                      but gated by the staff module (bug #5 follow-up). */}
                  <Route path="/location/:locationId/staff/:personId" element={<ModulePageGuard module="staff"><PersonProfilePage /></ModulePageGuard>} />

                  {/* Users (location-scope mirror of /company/users).
                      Thin wrapper around UsersRolesPage with scope='location'.
                      Gated by `location.manage_users` for this location, or
                      the company codename — administering a site's people is
                      site administration, not tenant administration. */}
                  <Route path="/location/:locationId/users" element={<ModulePageGuard module="users"><LocationUsersPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/users/:id" element={<ModulePageGuard module="users"><CompanyUserProfilePage /></ModulePageGuard>} />

                  {/* Management — admin cluster on the location rail.
                      Overview wraps the existing CompanyLocationOverview
                      scoped to this location; Users & Roles lives at the
                      :locationId/users routes above (mirrored in the
                      management panel for discoverability). */}
                  <Route path="/location/:locationId/management" element={<ProtectedPage><Navigate to="overview" replace /></ProtectedPage>} />
                  <Route path="/location/:locationId/management/overview" element={<ManagementPageGuard><LocationOverviewPage /></ManagementPageGuard>} />

                  {/* HR > Shifts */}
                  <Route path="/location/:locationId/shifts" element={<ModulePageGuard module="shifts"><ShiftsPage /></ModulePageGuard>} />

                  {/* HR > Payroll */}
                  <Route path="/location/:locationId/payroll" element={<ModulePageGuard module="payroll"><PayrollPage /></ModulePageGuard>} />

                  {/* Treasury — scope-in-URL routes (2026-05-22). The
                      path carries :locationId so chrome, page filters,
                      and apiClient all derive the active location from
                      the same source of truth. Legacy URLs without id
                      are handled by LegacyLocationCatchAll at the end
                      of the /location/* group. */}
                  <Route path="/location/:locationId/treasury" element={<TreasuryPageGuard><Navigate to="overview" replace /></TreasuryPageGuard>} />
                  <Route path="/location/:locationId/treasury/overview" element={<TreasuryPageGuard><TreasuryOverviewPage /></TreasuryPageGuard>} />
                  {/* Banking — read-only operational visualization
                      (balances, last sync, recent movements). Setup of
                      banks (add/link/configure) lives under
                      /location/:locationId/settings/treasury (Settings module). */}
                  <Route path="/location/:locationId/treasury/banking" element={<TreasuryPageGuard><TreasuryBankingPage /></TreasuryPageGuard>} />
                  <Route path="/location/:locationId/treasury/operations" element={<TreasuryPageGuard><TreasuryPage /></TreasuryPageGuard>} />
                  <Route path="/location/:locationId/treasury/pending" element={<TreasuryPageGuard><TreasuryPendingPage /></TreasuryPageGuard>} />
                  <Route path="/location/:locationId/treasury/cash" element={<TreasuryPageGuard><TreasuryCashPage /></TreasuryPageGuard>} />
                  {/* Reconciliation now lives as a tab inside Banking
                      (/treasury/banking?tab=reconciliation). The
                      standalone URL redirects so external bookmarks
                      keep working. TreasuryReconciliationPage stays
                      lazy-imported because its ReconciliationPanel
                      export is consumed by TreasuryBankingPage. */}
                  <Route path="/location/:locationId/treasury/reconciliation" element={<Navigate to="../banking?tab=reconciliation" replace />} />
                  <Route path="/location/:locationId/treasury/reports" element={<TreasuryPageGuard><TreasuryReportsPage /></TreasuryPageGuard>} />

                  {/* Management > Config */}
                  <Route path="/location/:locationId/settings" element={<SettingsEntryPage />} />
                  <Route path="/location/:locationId/settings/general" element={<SettingsPageGuard><LocationSettingsPage initialSection="general" /></SettingsPageGuard>} />
                  <Route path="/location/:locationId/settings/operating-hours" element={<SettingsPageGuard><LocationSettingsPage initialSection="operating_hours" /></SettingsPageGuard>} />
                  <Route path="/location/:locationId/settings/capacity-limits" element={<SettingsPageGuard><LocationSettingsPage initialSection="capacity_limits" /></SettingsPageGuard>} />
                  <Route path="/location/:locationId/settings/guest-form" element={<SettingsPageGuard><LocationSettingsPage initialSection="guest_form" /></SettingsPageGuard>} />
                  <Route path="/location/:locationId/settings/seasons" element={<SettingsPageGuard><LocationSettingsPage initialSection="seasons" /></SettingsPageGuard>} />
                  <Route path="/location/:locationId/settings/monetary" element={<SettingsPageGuard><LocationSettingsPage initialSection="monetary" /></SettingsPageGuard>} />
                  <Route path="/location/:locationId/settings/guest-payment" element={<SettingsPageGuard><LocationSettingsPage initialSection="guest_payment" /></SettingsPageGuard>} />
                  <Route path="/location/:locationId/settings/staff" element={<SettingsPageGuard><LocationSettingsPage initialSection="staff" /></SettingsPageGuard>} />
                  <Route path="/location/:locationId/settings/billing" element={<SettingsPageGuard><LocationSettingsPage initialSection="billing" /></SettingsPageGuard>} />
                  <Route path="/location/:locationId/invoices" element={<ModulePageGuard module="invoices"><LocationInvoicesPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/settings/contact" element={<SettingsPageGuard><LocationSettingsPage initialSection="contact" /></SettingsPageGuard>} />
                  {/* R-FE-1.8 tombstone: BanksPaymentsSection is replaced
                      by the new Treasury Settings hub Bancos tab. */}
                  <Route path="/location/:locationId/settings/banks" element={<Navigate to="../treasury?tab=banks" replace />} />
                  {/* R-FE-1: Treasury settings hub — bancos · cashboxes ·
                      tarjetas · procesadores · TPVs · routing. */}
                  <Route path="/location/:locationId/settings/treasury" element={<TreasuryPageGuard capability="manage"><TreasurySetupPage /></TreasuryPageGuard>} />
                  <Route path="/location/:locationId/settings/integrations" element={<SettingsPageGuard><LocationSettingsPage initialSection="integrations" /></SettingsPageGuard>} />
                  <Route path="/location/:locationId/settings/integrations/bookinglayer" element={<SettingsPageGuard><BookinglayerStatusPage /></SettingsPageGuard>} />
                  <Route path="/location/:locationId/settings/integrations/bookinglayer/connect" element={<SettingsPageGuard><BookinglayerConnectPage /></SettingsPageGuard>} />
                  {/* The Integrations settings page renders no Stripe card while
                      STRIPE_AVAILABLE is off, so this page is only reachable by
                      typing the URL — and it cannot connect anything. */}
                  {STRIPE_AVAILABLE && (
                    <Route path="/location/:locationId/settings/integrations/stripe/connect" element={<SettingsPageGuard><StripeConnectPage /></SettingsPageGuard>} />
                  )}
                  {/* Stripe pages resolve their integrationId from
                      (a) ?integrationId= query param (preferred — set by
                      links from Banking page) or (b) the first active
                      Stripe PaymentIntegration if no query param. */}
                  {/* Same treasury RBAC as the Banking surfaces that link
                      here — ledger/payouts expose processor money data. */}
                  <Route path="/location/:locationId/integrations/stripe/ledger" element={<TreasuryPageGuard><StripeLedgerPage /></TreasuryPageGuard>} />
                  <Route path="/location/:locationId/integrations/stripe/payouts" element={<TreasuryPageGuard><StripePayoutsPage /></TreasuryPageGuard>} />
                  {/* Pre-scope-in-URL bookmarks. The pages read location-scoped
                      treasury data, so they cannot resolve outside a location:
                      hand them to the hint-based rewriter like any other
                      legacy /location URL. */}
                  <Route path="/integrations/stripe/ledger" element={<LegacyStripeRedirect />} />
                  <Route path="/integrations/stripe/payouts" element={<LegacyStripeRedirect />} />

                  {/* Management > Reports */}

                  {/* User profile */}
                  {/* /notifications is YOURS. The two below are shared queues
                      belonging to a site and to the company — same module,
                      different owner. Neither carries a CATEGORY permission
                      gate: the server already filters every row by the
                      permission that guards its category, so an empty page is
                      the correct answer for someone with no matching grants,
                      and a blanket gate would hide categories they CAN see.
                      The location inbox does check access to the location in
                      the path — an inbox for a site you cannot reach is not
                      an empty page, it is the wrong site. */}
                  <Route path="/notifications" element={<ProtectedPage><NotificationsPage /></ProtectedPage>} />
                  <Route path="/location/:locationId/notifications" element={<LocationAccessPageGuard><LocationNotificationsPage /></LocationAccessPageGuard>} />
                  <Route path="/settings/notifications" element={<ProtectedPage><NotificationPreferencesPage /></ProtectedPage>} />
                  {/* My profile (self-service "My account" — distinct from the
                      operational PersonProfile). PersonProfilePage stays
                      reachable at /persons/me for editing the linked Person row. */}
                  <Route path="/profile" element={<ProtectedPage><MyProfilePage /></ProtectedPage>} />
                  {/* Help & support — tenant-side ticket flow. UserMenu
                      links here from the avatar dropdown. */}
                  <Route path="/help" element={<ProtectedPage><HelpPage /></ProtectedPage>} />
                  <Route path="/persons/me" element={<ProtectedPage><PersonProfilePage /></ProtectedPage>} />

                  {/* Locations */}
                  <Route path="/locations" element={<ProtectedPage><LocationsPage /></ProtectedPage>} />

                  {/* Commerce > Suppliers */}
                  <Route path="/location/:locationId/suppliers" element={<ModulePageGuard module="suppliers"><SuppliersPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/suppliers/purchase-orders" element={<ModulePageGuard module="suppliers"><SuppliersPurchaseOrdersPage /></ModulePageGuard>} />
                  <Route path="/location/:locationId/suppliers/history" element={<ModulePageGuard module="suppliers"><SuppliersHistoryPage /></ModulePageGuard>} />

                  {/* /inventory retired (PENDINGS #28): it duplicated the Bar & Kitchen
                      surfaces with a divergent product editor. Deep links land on the
                      canonical products page; category management moved there too. */}
                  <Route path="/location/:locationId/inventory" element={<Navigate to="../bar-kitchen/products" replace />} />

                  <Route path="/logout" element={<ProtectedRoute><Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><LogoutPage /></Suspense></ProtectedRoute>} />

                  {/* Company routes — own layout */}
                  <Route path="/company" element={<Navigate to="/company/overview" replace />} />
                  <Route path="/company/overview" element={<CompanyPage><ManagementOverviewPage /></CompanyPage>} />
                  {/* Retired 2026-04-28 — the Locations grid view was a
                      lighter version of the same data. Locations list owns
                      the entry; per-location detail pages remain. */}
                  <Route path="/company/locations/overview" element={<Navigate to="/company/locations" replace />} />
                  <Route path="/company/locations" element={<CompanyModulePageGuard module="locations"><CompanyLocationsPage /></CompanyModulePageGuard>} />
                  <Route path="/company/locations/:id" element={<CompanyModulePageGuard module="locations"><CompanyLocationDetailPage /></CompanyModulePageGuard>} />
                  <Route path="/company/locations/:id/settings" element={<CompanyModulePageGuard module="locations"><CompanyLocationSettingsPage /></CompanyModulePageGuard>} />
                  <Route path="/company/treasury" element={<TreasuryPageGuard scopeType="company"><CompanyTreasuryPage /></TreasuryPageGuard>} />
                  {/* Retired 2026-04-28 — Banks is now a tab inside Treasury.
                      Redirect catches the legacy direct link. */}
                  <Route path="/company/banks" element={<Navigate to="/company/treasury" replace />} />
                  <Route path="/company/invoices" element={<CompanyModulePageGuard module="invoices"><CompanyInvoicesPage /></CompanyModulePageGuard>} />
                  {/* One company people list. `/company/staff` and
                      `/company/users` described the same people from two
                      sides; both now land here filtered, so there is a single
                      implementation of the table. Old links keep working.
                      Guarded by `staff`, whose permission set already
                      subsumes `company.manage_users` — so everyone who could
                      reach either retired surface still gets in. */}
                  <Route path="/company/people" element={<CompanyModulePageGuard module="staff"><CompanyPeoplePage /></CompanyModulePageGuard>} />
                  {/* `?tab=roles` still renders here; anything else forwards
                      to People filtered to account holders (see the page). */}
                  <Route path="/company/users" element={<CompanyModulePageGuard module="users"><CompanyUsersPage /></CompanyModulePageGuard>} />
                  <Route path="/company/users/:id" element={<CompanyModulePageGuard module="users"><CompanyUserProfilePage /></CompanyModulePageGuard>} />
                  <Route path="/company/staff" element={<Navigate to="/company/people?filter=employees" replace />} />
                  <Route path="/company/staff/positions" element={<CompanyModulePageGuard module="staff"><CompanyStaffPositionsPage /></CompanyModulePageGuard>} />
                  <Route path="/company/payroll" element={<CompanyModulePageGuard module="payroll"><CompanyPayrollPage /></CompanyModulePageGuard>} />
                  <Route path="/company/tokens" element={<CompanyPage><TokensCompanyPage /></CompanyPage>} />
                  <Route path="/company/notifications" element={<CompanyPage><CompanyNotificationsPage /></CompanyPage>} />
                  {/* Each company-settings sub-route renders the same page
                      with an explicit `initialSection`. The rail's submenu
                      under Admin → Settings is the single source of
                      navigation — there is no in-page tab strip. The bare
                      /company/settings URL redirects to General so old
                      links still land somewhere meaningful. */}
                  <Route path="/company/settings" element={<Navigate to="/company/settings/general" replace />} />
                  <Route path="/company/settings/general" element={<CompanyModulePageGuard module="locations"><CompanySettingsPage initialSection="general" /></CompanyModulePageGuard>} />
                  <Route path="/company/settings/billing" element={<CompanyModulePageGuard module="locations"><CompanySettingsPage initialSection="billing" /></CompanyModulePageGuard>} />
                  <Route path="/company/settings/integrations" element={<CompanyModulePageGuard module="integrations"><CompanySettingsPage initialSection="integrations" /></CompanyModulePageGuard>} />
                  <Route path="/company/settings/tokens" element={<CompanyModulePageGuard module="staff"><CompanySettingsPage initialSection="tokens" /></CompanyModulePageGuard>} />
                  <Route path="/company/settings/emails" element={<CompanyModulePageGuard module="locations"><CompanySettingsPage initialSection="emails" /></CompanyModulePageGuard>} />

                  {/* Dev-only — design tokens reference (only available in development builds) */}
                  {import.meta.env.DEV && (
                    <Route path="/_dev/tokens" element={<Suspense fallback={<LoadingState />}><DevTokensPage /></Suspense>} />
                  )}

                  {/* Backward-compat redirects (2026-04-25 location-scope URL split).
                      Every location-scoped surface moved from /X to /location/X.
                      These splat redirects catch the legacy paths and preserve
                      the full sub-path + search + hash via RedirectToLocationPath. */}
                  <Route path="/frontdesk/*" element={<RedirectToLocationPath />} />
                  <Route path="/bookings/*" element={<RedirectToLocationPath />} />
                  <Route path="/guests/*" element={<RedirectToLocationPath />} />
                  <Route path="/rooms/*" element={<RedirectToLocationPath />} />
                  <Route path="/activities/*" element={<RedirectToLocationPath />} />
                  <Route path="/products-sales/*" element={<RedirectToLocationPath />} />
                  <Route path="/pos/*" element={<RedirectToLocationPath />} />
                  <Route path="/sales/*" element={<RedirectToLocationPath />} />
                  <Route path="/bar-kitchen/*" element={<RedirectToLocationPath />} />
                  <Route path="/shop/*" element={<RedirectToLocationPath />} />
                  <Route path="/suppliers/*" element={<RedirectToLocationPath />} />
                  <Route path="/inventory/*" element={<RedirectToLocationPath />} />
                  <Route path="/hr/*" element={<RedirectToLocationPath />} />
                  <Route path="/staff/*" element={<RedirectToLocationPath />} />
                  <Route path="/shifts/*" element={<RedirectToLocationPath />} />
                  <Route path="/payroll/*" element={<RedirectToLocationPath />} />
                  <Route path="/treasury/*" element={<RedirectToLocationPath />} />
                  {/* /settings stays partly neutral: /settings/notifications is the
                      USER notification preferences page, not part of the location
                      settings tree. So we don't splat-redirect /settings/* — only
                      the bare /settings root migrates. */}
                  <Route path="/settings" element={<Navigate to="/location/settings" replace />} />
                  <Route path="/settings/general" element={<Navigate to="/location/settings/general" replace />} />
                  <Route path="/settings/seasons" element={<Navigate to="/location/settings/seasons" replace />} />
                  <Route path="/settings/staff" element={<Navigate to="/location/settings/staff" replace />} />
                  <Route path="/settings/billing" element={<Navigate to="/location/settings/billing" replace />} />
                  <Route path="/settings/contact" element={<Navigate to="/location/settings/contact" replace />} />
                  <Route path="/settings/banks" element={<Navigate to="/location/settings/treasury?tab=banks" replace />} />
                  <Route path="/settings/integrations/*" element={<RedirectToLocationPath />} />

                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  {/* Backwards-compat catch-all for legacy `/location/*`
                      URLs (no :locationId). The redirect injects the
                      last-seen location id from localStorage; if no
                      hint exists, sends the user to /select. Lives
                      until ~2026-11 per docs/plan/SCOPE_IN_URL.md. */}
                  <Route path="/location/*" element={<LegacyLocationCatchAll />} />
                  <Route path="*" element={<Suspense fallback={<LoadingState message={APP_LOADING_MESSAGE} />}><NotFoundPage /></Suspense>} />
                </Routes>
                </TenantProvider>
                </ActiveContextProvider>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
